// Видимость и порядок колонок по шаблону, который приходит с сервера.
//
// У заказчика 27 разных раскладок колонок при точном сравнении заголовков (пересчёт по майской
// книге 10 августа, `tools/datagen`). Двадцать восьмая появится ровно тогда, когда он заведёт
// новый объект, — и она обязана стоить НОЛЬ строк кода.
//
// Отсюда устройство: каталог колонок в системе ОДИН на все коды, а раскладка — фильтр и
// сортировка над ним. Не ветки «если объектовая — так, если административная — иначе»: двадцать
// семь веток невозможно ни прочитать, ни проверить, и каждая новая раскладка требовала бы
// правки кода и выпуска версии.
//
// Скрытая колонка отсутствует ВЕЗДЕ: и в разметке, и в обходе клавиатурой, и в копировании в
// буфер, и в разборе вставки. Это не оптимизация, а требование к деньгам: колонка, невидимая
// глазом, но живая в буфере, увозит в файл заказчика суммы, которых он в ведомости не видел.
// Поэтому шаблон применяется ОДИН раз, на входе в сетку, а не в каждом месте по отдельности.

import type { GridColumn } from './types';

/** Раскладка колонок объекта: что показывать, в каком порядке и под какими заголовками. */
export interface ColumnLayout {
  /**
   * Коды колонок в порядке шаблона. Колонки, которой здесь нет, нет и в сетке.
   *
   * Неизвестный код молча пропускается: сервер знает про колонку, которой в этой версии
   * программы ещё нет, и новый шаблон не имеет права обнулить человеку ведомость.
   */
  readonly visible: readonly string[];
  /**
   * Заголовки из печатной формы заказчика.
   *
   * Нужны потому, что его заголовки местами стоят обратно смыслу слов: «Официально получать» в
   * объектовой ведомости держит уже выплаченное, а в административной — наоборот. Хранится
   * значение по смыслу, а показывается и печатается заголовок его шаблона; переименовать их
   * нельзя — на печать уходит его бланк.
   */
  readonly titles?: Readonly<Record<string, string>>;
}

/**
 * Наложить раскладку на каталог колонок.
 *
 * Пусто — каталог отдаётся как есть: табель и официальный контур шаблонов не имеют, и заводить
 * им пустую раскладку значило бы описывать одно и то же дважды.
 */
export function applyColumnLayout<TRow>(
  columns: readonly GridColumn<TRow>[],
  layout: ColumnLayout | null | undefined,
): readonly GridColumn<TRow>[] {
  if (layout === null || layout === undefined) {
    return columns;
  }

  const поКоду = new Map(columns.map((колонка, номер) => [колонка.id, номер] as const));
  const названные = layout.visible
    .map((код) => поКоду.get(код))
    .filter((номер): номер is number => номер !== undefined);
  const названо = new Set(названные);

  // Колонки вне шаблона: фамилия слева, итог справа. Шаблон заказчика перечисляет только
  // денежные колонки, а без фамилии ведомость перестаёт быть ведомостью.
  //
  // Место им отводится по каталогу: те, что в каталоге стоят раньше ЛЮБОЙ шаблонной, идут
  // слева, остальные справа. Правило простое и одинаковое для всех раскладок — тем оно и
  // ценно: двадцать восьмая раскладка не потребует решать этот вопрос заново.
  const перваяШаблонная = названные.length === 0 ? columns.length : Math.min(...названные);
  const слева: GridColumn<TRow>[] = [];
  const справа: GridColumn<TRow>[] = [];
  columns.forEach((колонка, номер) => {
    if (колонка.always !== true || названо.has(номер)) {
      return;
    }
    (номер < перваяШаблонная ? слева : справа).push(колонка);
  });

  const шаблонные = названные.map((номер) => columns[номер]!);
  return [...слева, ...шаблонные, ...справа].map((колонка) => {
    const свой = layout.titles?.[колонка.id];
    return свой === undefined ? колонка : { ...колонка, title: свой };
  });
}
